import { useEffect, useState } from 'react';

import { getTaxonomy } from './api';
import { ICONS } from './icons.generated';
import type { VocabularyRef } from './opportunity';
import { EVENT_TYPES, GOALS, TOPICS } from './taxonomy';

/**
 * The topics, goals and event-type words, from `GET /taxonomy`.
 *
 * Fetched once per page load and shared, with `taxonomy.ts`'s lists standing in
 * until it answers, so the sidebar never waits on it.
 */

export interface Vocabulary {
  topics: VocabularyRef[];
  /** Only the goals the site draws; the backend's vocabulary has some no event uses. */
  goals: VocabularyRef[];
  eventTypes: string[];
  topicLabel: (key: string) => string;
  goalLabel: (key: string) => string;
}

function build(topics: VocabularyRef[], goals: VocabularyRef[], eventTypes: string[]): Vocabulary {
  const topicLabels = new Map(topics.map((t) => [t.key, t.label]));
  const goalLabels = new Map(goals.map((g) => [g.key, g.label]));
  return {
    topics,
    goals,
    eventTypes,
    topicLabel: (key) => topicLabels.get(key) ?? key,
    goalLabel: (key) => goalLabels.get(key) ?? key,
  };
}

const FALLBACK = build(TOPICS, GOALS, EVENT_TYPES);

let request: Promise<Vocabulary> | null = null;
let settled: Vocabulary | null = null;

const bySortOrder = <T extends { sortOrder: number }>(list: T[]) =>
  [...list].sort((a, b) => a.sortOrder - b.sortOrder);

function load(): Promise<Vocabulary> {
  if (!request) {
    request = getTaxonomy()
      .then((taxonomy) => {
        settled = build(
          bySortOrder(taxonomy.topics),
          bySortOrder(taxonomy.goals).filter((g) => ICONS[`goal:${g.key}`]),
          taxonomy.eventTypes?.length ? taxonomy.eventTypes : EVENT_TYPES,
        );
        return settled;
      })
      .catch(() => {
        // Try again on the next mount; the fallback serves meanwhile.
        request = null;
        return FALLBACK;
      });
  }
  return request;
}

export function useTaxonomy(): Vocabulary {
  const [vocabulary, setVocabulary] = useState<Vocabulary>(() => settled ?? FALLBACK);

  useEffect(() => {
    let live = true;
    void load().then((v) => {
      if (live) setVocabulary(v);
    });
    return () => {
      live = false;
    };
  }, []);

  return vocabulary;
}
